import { useCallback, useEffect, useRef, useState } from 'react';
import { listConversationMessages } from '@/lib/messages.ts';
import type { ConversationOverview, Message } from '@/types/messages.ts';
import { orderedConversations, type ConversationGroup } from './conversationGroups.ts';

/*
 * A transcript is read backwards a page at a time, and a channel's transcript
 * runs across several conversations — so what is on screen is an accumulation
 * rather than the answer to one request, and react-query has nothing to cache
 * that the next scroll would not throw away. The state therefore lives here,
 * exactly as it did in the Vue view: a queue of conversations newest first, and
 * how far into each one we have read.
 */

interface Reader {
  conversations: ConversationOverview[];
  /** Which conversation of the queue the last page came from. */
  index: number;
  /** The last page fetched, per conversation. */
  pages: Map<string, number>;
  /** Whether the conversation at `index` goes back further still. */
  moreInCurrent: boolean;
}

function idle(): Reader {
  return { conversations: [], index: 0, pages: new Map(), moreInCurrent: false };
}

/** What has been read, and which selection it was read for. */
interface Loaded {
  key: string | null;
  messages: Message[];
  hasMore: boolean;
  failed: boolean;
}

export interface Transcript {
  /** Oldest first, which is the order a conversation is read in. */
  messages: Message[];
  loading: boolean;
  loadingMore: boolean;
  /** Whether the conversation goes back further than what is here. */
  hasMore: boolean;
  /** Reads one more page, older, onto the front. */
  loadMore: () => void;
  /** Whether several conversations are being read as one. */
  merged: boolean;
  /** Whether the first page could not be read. */
  failed: boolean;
  /** Reads the first page again, after it failed. */
  retry: () => void;
}

/**
 * Everything said in the selected conversation — or, for a channel, in every
 * conversation on it, newest first and older pages loaded as they are reached.
 */
export function useTranscript(selection: ConversationGroup | null): Transcript {
  const [loaded, setLoaded] = useState<Loaded>({
    key: null,
    messages: [],
    hasMore: false,
    failed: false,
  });
  const [attempt, setAttempt] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const reader = useRef<Reader>(idle());
  /* Bumped on every selection change, so a slow answer to an old one is dropped. */
  const run = useRef(0);
  const busy = useRef(false);

  /* A channel's group id is the same for every person, so the conversations name it. */
  const key = selection ? selection.conversations.map((one) => one.id).join(' ') : null;
  /* Nothing has been read for this selection yet, so nothing of it is shown. */
  const current = loaded.key === key;

  useEffect(() => {
    const token = ++run.current;
    busy.current = false;
    reader.current = idle();

    if (!selection || selection.conversations.length === 0) {
      return;
    }

    const conversations = orderedConversations(selection);
    reader.current = { conversations, index: 0, pages: new Map(), moreInCurrent: false };

    void (async () => {
      try {
        const first = conversations[0];
        const answer = await listConversationMessages(first.id, 1);
        if (token !== run.current) return;

        reader.current.pages.set(first.id, 1);
        reader.current.moreInCurrent = answer.meta.currentPage < answer.meta.totalPages;
        setLoaded({
          key,
          messages: [...answer.data].reverse(),
          hasMore: reader.current.moreInCurrent || conversations.length > 1,
          failed: false,
        });
      } catch {
        if (token !== run.current) return;
        setLoaded({ key, messages: [], hasMore: false, failed: true });
      }
    })();
  }, [selection, key, attempt]);

  const loadMore = useCallback(() => {
    const state = reader.current;
    if (busy.current || state.conversations.length === 0) return;

    const token = run.current;
    busy.current = true;
    setLoadingMore(true);

    void (async () => {
      try {
        const conversation = state.conversations[state.index];
        let answer;

        if (state.moreInCurrent) {
          const next = (state.pages.get(conversation.id) ?? 1) + 1;
          answer = await listConversationMessages(conversation.id, next);
          if (token !== run.current) return;
          state.pages.set(conversation.id, next);
        } else if (state.index < state.conversations.length - 1) {
          state.index += 1;
          const older = state.conversations[state.index];
          answer = await listConversationMessages(older.id, 1);
          if (token !== run.current) return;
          state.pages.set(older.id, 1);
        } else {
          return;
        }

        state.moreInCurrent = answer.meta.currentPage < answer.meta.totalPages;
        const older = [...answer.data].reverse();

        setLoaded((shown) => ({
          ...shown,
          messages: [...older, ...shown.messages],
          hasMore: state.moreInCurrent || state.index < state.conversations.length - 1,
        }));
      } catch {
        /* Leave what is on screen alone; the next scroll asks again. */
      } finally {
        if (token === run.current) {
          busy.current = false;
          setLoadingMore(false);
        }
      }
    })();
  }, []);

  return {
    messages: current ? loaded.messages : [],
    loading: key !== null && !current,
    loadingMore,
    hasMore: current ? loaded.hasMore : false,
    loadMore,
    merged: (selection?.conversations.length ?? 0) > 1,
    failed: current && loaded.failed,
    retry: () => {
      setLoaded((shown) => ({ ...shown, key: null }));
      setAttempt((count) => count + 1);
    },
  };
}
